import { createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../../../app/api';
import { buildEncodedQueryString, convertToCSVFormat, CSVDataType, downloadCsv } from '../../../../common';
import { PagedResult } from '../../../../store';
import {
  ConsolidatedSecurityHoldingsSummary,
  ConsolidatedSecurityHoldingsSummaryThunkParameters,
  DownloadSecurityExposurePayload,
  SecurityExposureItem,
  SecurityExposureThunkParameters,
} from './types';

export enum SecurityAssociatedClientsActionTypes {
  FetchConsolidatedSecurityHoldingsSummary = '@@security/AssociatedClients/FetchConsolidatedSecurityHoldingsSummary',
  FetchSecurityExposure = '@@security/AssociatedClients/FetchSecurityExposure',
  DownloadSecurityExposure = '@@security/AssociatedClients/DownloadSecurityExposure',
}

export enum SecurityAssociatedClientsApiEndpoints {
  fetchConsolidatedSecurityHoldingsSummary = '/bff/GetConsolidatedSecurityHoldingsSummary',
  fetchSecurityExposure = '/bff/GetSecurityExposure',  
}

export const fetchConsolidatedSecurityHoldingsSummary = createAsyncThunk(
  SecurityAssociatedClientsActionTypes.FetchConsolidatedSecurityHoldingsSummary,
  async (wrapper: ConsolidatedSecurityHoldingsSummaryThunkParameters) => {
    const response = await api.get<ConsolidatedSecurityHoldingsSummary>(
      `${SecurityAssociatedClientsApiEndpoints.fetchConsolidatedSecurityHoldingsSummary}?securityId=${wrapper.securityId}&dateTime=${wrapper.dateTime}&isDataFromTriumph=${wrapper.isDataFromTriumph}`
    );
    return response.data;
  }
);

const getPagedSecurityExposure = async (request: SecurityExposureThunkParameters) => {
  const queryString = buildEncodedQueryString({
    pagedRequest: request.pagination,
    securityId: request.securityId,
    date: request.toDate,
  });
  return await api.get<PagedResult<SecurityExposureItem>>(
    `${SecurityAssociatedClientsApiEndpoints.fetchSecurityExposure}${queryString}`
  );
};

export const fetchSecurityExposure = createAsyncThunk(
  SecurityAssociatedClientsActionTypes.FetchSecurityExposure,
  async (wrapper: SecurityExposureThunkParameters) => {
    const response = await getPagedSecurityExposure(wrapper);
    return {
      results: response.data,
      pagination: wrapper.pagination,
    };
  }
);

export const downloadSecurityExposure = createAsyncThunk(
  SecurityAssociatedClientsActionTypes.DownloadSecurityExposure,
  async (wrapper: DownloadSecurityExposurePayload) => {
    const response = await getPagedSecurityExposure({
      ...wrapper,
      pagination: { ...wrapper.pagination, pageNumber: 1, pageSize: 9999999 },
    });
    const data: CSVDataType[] = convertToCSVFormat(response.data.results);

    const headerMapping = {
        afslName: "AFSL NAME",
        practiceName: "PRACTICE NAME",
        adviserName: "ADVISER NAME",
        clientId: "CLIENT ID",
        clientName: "CLIENT NAME",
        investmentServiceCode: "PORTFOLIO ID",
        investmentServiceName: "PORTFOLIO NAME",
        accountNumber: "ACCOUNT NUMBER",
        accountName: "ACCOUNT NAME",
        securityCode: "SECURITY CODE",
        unitPrice: "PRICE",
        units: "UNITS",
        value: "VALUE",
        priceDate: "PRICE DATE"
      } as const;

      const reorderedData = data.map((row) => {
      const newRow: CSVDataType = {};
      for (const key in headerMapping) {
        const typedKey = key as keyof typeof headerMapping; 
        const label = headerMapping[typedKey];
        newRow[label] = row[typedKey] ?? "";
      }
      return newRow;
    });

    if (reorderedData.length === 0) {
      const emptyRow: CSVDataType = {};
      for (const key in headerMapping) {
        const label = headerMapping[key as keyof typeof headerMapping];
        emptyRow[label] = "";
      }
      reorderedData.push(emptyRow);
    }

    downloadCsv(reorderedData, "SecurityExposure_");

    return { message: "CSV downloaded" };
  }
);
